import React from "react";
import { alpha, Box, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { FleetLocationFilterCards } from "../../../components/FleetManagement/FleetLocationFilterCards.partial";
import type { ResourceKind } from "../typings/resource-calendar.types";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import { ScheduleColleagueSelect } from "./ScheduleColleagueSelect.partial";
import {
  FirstAidOptions,
  ScheduleFirstAidSelect,
  SelectFirstAidOptions
} from "./ScheduleFirstAidSelect.partial";
import { ScheduleMode, ScheduleModeToggle } from "./ScheduleModeToggle.partial";
import {
  ScheduleResourceAutocomplete,
  type ScheduleResourceAutocompleteOption
} from "./ScheduleResourceAutocomplete.partial";
import { ScheduleResourceBookingTypeControls } from "./ScheduleResourceBookingTypeControls.partial";
import { ScheduleViewMode, ScheduleViewToggle } from "./ScheduleViewToggle.partial";
import {
  isStandaloneResourceBookingActive,
  resourcePlaceBookingTypes,
  shouldShowResourceKindToggle
} from "../utils/resource-booking-schedule.utils";

export const scheduleToolbarPaperSx = (theme: Theme) => ({
  p: 2,
  mb: 1.5,
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04)
});

export type ScheduleToolbarProps = {
  scheduleMode: ScheduleMode;
  onScheduleModeChange: (mode: ScheduleMode) => void;
  scheduleViewMode: ScheduleViewMode;
  onScheduleViewModeChange: (mode: ScheduleViewMode) => void;
  /** When set, replaces the default calendar/table view toggle (e.g. dashboard week/calendar/table). */
  viewToggleSlot?: React.ReactNode;
  multiDeleteSlot?: React.ReactNode;
  isColleagueMode: boolean;
  isResourceMode: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  tagLimit: number;
  colleagues: TeamMember[];
  selectedColleagues: TeamMember[];
  scheduleColleaguesIsFetching: boolean;
  storedScheduleColleaguesIsFetching: boolean;
  preStoredColleagues: string[];
  isFirstAidSelected: boolean;
  onSelectedColleaguesChange: (colleagues: TeamMember[]) => void;
  onStoreSelectedColleagues: () => void;
  useFirstAidUserFilter?: boolean;
  firstAidOptions?: FirstAidOptions[];
  selectFirstAidOptions?: SelectFirstAidOptions;
  onSelectFirstAidOptionsChange?: (options: SelectFirstAidOptions) => void;
  resourceAvailableTypes: BookingType[];
  resourceBookingType: BookingType | null;
  onResourceBookingTypeChange: (type: BookingType | null) => void;
  onResourceFetchEnable?: () => void;
  resourceAutocompleteLoading: boolean;
  bookableBookingObjectsError: boolean;
  resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[];
  selectedResource: ScheduleResourceAutocompleteOption | null;
  onSelectedResourceChange: (resource: ScheduleResourceAutocompleteOption | null) => void;
  resourceLocationCards?: Array<{ id: number; name: string; picturePath?: string | null }>;
  selectedResourceLocationIds?: number[];
  onToggleResourceLocation?: (locationId: number) => void;
  onClearResourceLocationFilter?: () => void;
  resourceKind?: ResourceKind;
  onResourceKindChange?: (kind: ResourceKind) => void;
};

export function ScheduleToolbar({
  scheduleMode,
  onScheduleModeChange,
  scheduleViewMode,
  onScheduleViewModeChange,
  viewToggleSlot,
  multiDeleteSlot,
  isColleagueMode,
  isResourceMode,
  containerRef,
  tagLimit,
  colleagues,
  selectedColleagues,
  scheduleColleaguesIsFetching,
  storedScheduleColleaguesIsFetching,
  preStoredColleagues,
  isFirstAidSelected,
  onSelectedColleaguesChange,
  onStoreSelectedColleagues,
  useFirstAidUserFilter = false,
  firstAidOptions = [],
  selectFirstAidOptions = { options: [], reason: null },
  onSelectFirstAidOptionsChange,
  resourceAvailableTypes,
  resourceBookingType,
  onResourceBookingTypeChange,
  onResourceFetchEnable,
  resourceAutocompleteLoading,
  bookableBookingObjectsError,
  resourceAutocompleteOptions,
  selectedResource,
  onSelectedResourceChange,
  resourceLocationCards = [],
  selectedResourceLocationIds = [],
  onToggleResourceLocation,
  onClearResourceLocationFilter,
  resourceKind = "equipment",
  onResourceKindChange
}: Readonly<ScheduleToolbarProps>) {
  const placeBookingTypes = resourcePlaceBookingTypes(resourceAvailableTypes);
  const showResourceKindToggle = shouldShowResourceKindToggle(
    isResourceMode,
    resourceAvailableTypes,
    onResourceKindChange
  );
  const showResourceLocationFilter =
    isResourceMode &&
    isStandaloneResourceBookingActive(resourceBookingType) &&
    resourceLocationCards.length > 0 &&
    onToggleResourceLocation != null &&
    onClearResourceLocationFilter != null;

  return (
    <Stack spacing={showResourceLocationFilter ? 1.5 : 0} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        useFlexGap
        spacing={2}
        data-testid="schedule-mode-toggle-and-colleague-field-container"
      >
        {multiDeleteSlot}

        <ScheduleModeToggle scheduleMode={scheduleMode} setScheduleMode={onScheduleModeChange} />

        {viewToggleSlot ?? (
          <ScheduleViewToggle
            scheduleViewMode={scheduleViewMode}
            setScheduleViewMode={onScheduleViewModeChange}
          />
        )}

        {isColleagueMode && colleagues.length >= 1 && (
          <ScheduleColleagueSelect
            containerRef={containerRef}
            tagLimit={tagLimit}
            colleagues={colleagues}
            selectedColleagues={selectedColleagues}
            scheduleColleaguesIsFetching={scheduleColleaguesIsFetching}
            storedScheduleColleaguesIsFetching={storedScheduleColleaguesIsFetching}
            preStoredColleagues={preStoredColleagues}
            isFirstAidSelected={isFirstAidSelected}
            setSelectedColleagues={onSelectedColleaguesChange}
            handleStoredScheduleColleagues={onStoreSelectedColleagues}
          />
        )}

        {isColleagueMode &&
          isFirstAidSelected &&
          useFirstAidUserFilter &&
          onSelectFirstAidOptionsChange && (
            <ScheduleFirstAidSelect
              containerRef={containerRef}
              tagLimit={tagLimit}
              firstAidOptions={firstAidOptions}
              selectFirstAidOptions={selectFirstAidOptions}
              setSelectFirstAidOptions={onSelectFirstAidOptionsChange}
            />
          )}

        {isResourceMode && (
          <>
            <ScheduleResourceBookingTypeControls
              buttonStyle={{ fontSize: 32 }}
              iconSize="24px"
              placeBookingTypes={placeBookingTypes}
              resourceBookingType={resourceBookingType}
              resourceKind={resourceKind}
              showResourceKindToggle={showResourceKindToggle}
              onResourceBookingTypeChange={onResourceBookingTypeChange}
              onResourceKindChange={onResourceKindChange}
              onResourceFetchEnable={onResourceFetchEnable}
            />
            <ScheduleResourceAutocomplete
              isLoading={resourceAutocompleteLoading}
              loadError={bookableBookingObjectsError}
              options={resourceAutocompleteOptions}
              value={selectedResource}
              resourceBookingType={resourceBookingType}
              resourceKind={resourceKind}
              onResourceChange={value => {
                onSelectedResourceChange(value);
              }}
            />
          </>
        )}
      </Stack>

      {showResourceLocationFilter && (
        <Box data-testid="schedule-resource-location-filter">
          <FleetLocationFilterCards
            displayMode="scroll"
            locations={resourceLocationCards}
            selectedLocationIds={selectedResourceLocationIds}
            onToggleLocation={onToggleResourceLocation}
            onClear={onClearResourceLocationFilter}
          />
        </Box>
      )}
    </Stack>
  );
}
